import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LEGAL_DOCUMENTS, DOCUMENT_NAMES, PROFILING_CONSENT_VERSION, getDocumentPath, getChangesPath, MANDATORY_DOCUMENT_TYPES, MandatoryDocumentType } from '../config/legalDocuments';
import { config } from '../config';
import { useMsal } from '@azure/msal-react';

export interface PendingDocument {
    type: 'tos' | 'privacy' | 'ai_notice';
    currentVersion: string;
    userAcceptedVersion: string | null;
}

export interface ProfilingConsentStatus {
    currentVersion: string;
    userAcceptedVersion: string | null;
    acceptedAt: string | null;
    withdrawnAt: string | null;
    isActive: boolean;
}

interface LegalConsentModalProps {
    pendingDocuments: PendingDocument[];
    profilingConsent?: ProfilingConsentStatus | null;
    onAcceptAll: () => void;
    getAuthToken: () => Promise<string | null>;
}

const LegalConsentModal: React.FC<LegalConsentModalProps> = ({
    pendingDocuments,
    profilingConsent,
    onAcceptAll,
    getAuthToken
}) => {
    const { instance } = useMsal();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showFullDocument, setShowFullDocument] = useState<{ type: string; content: string } | null>(null);
    const [documentsChanges, setDocumentsChanges] = useState<Record<string, string>>({});

    // Detect scenario
    const isFirstTime = pendingDocuments.every(doc => doc.userAcceptedVersion === null);

    // Profiling consent is only offered if user doesn't already have active profiling
    const showProfilingOption = !profilingConsent?.isActive;

    // Load "what's new" markdown for updated documents (auto-detected from version)
    useEffect(() => {
        const loadChanges = async () => {
            const changes: Record<string, string> = {};

            for (const doc of pendingDocuments) {
                if (doc.userAcceptedVersion === null) continue;

                try {
                    const changesUrl = getChangesPath(doc.type, doc.currentVersion);
                    const response = await fetch(changesUrl);
                    if (response.ok) {
                        const contentType = response.headers.get('content-type') || '';
                        if (contentType.includes('text/html')) continue;
                        const text = await response.text();
                        if (text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<html')) continue;
                        changes[doc.type] = text;
                    }
                } catch (err) {
                    // No changes file for this version — that's fine, skip
                }
            }

            setDocumentsChanges(changes);
        };

        loadChanges();
    }, [pendingDocuments]);

    // Load full document when requested
    const handleViewFullDocument = async (type: string) => {
        try {
            const documentUrl = getDocumentPath(type);
            const response = await fetch(documentUrl);
            if (response.ok) {
                const contentType = response.headers.get('content-type') || '';
                const content = await response.text();
                if (contentType.includes('text/html') || content.trimStart().startsWith('<!DOCTYPE')) {
                    setError('Nepodarilo sa načítať dokument');
                    return;
                }
                setShowFullDocument({ type, content });
            } else {
                setError('Nepodarilo sa načítať dokument');
            }
        } catch (err) {
            console.error('Failed to load document:', err);
            setError('Nepodarilo sa načítať dokument');
        }
    };

    const handleLogout = () => {
        instance.logoutRedirect();
    };

    // Accept mandatory documents only
    const handleAcceptMandatory = async () => {
        await doAccept(false);
    };

    // Accept mandatory + profiling
    const handleAcceptAll = async () => {
        await doAccept(true);
    };

    const doAccept = async (includesProfiling: boolean) => {
        setLoading(true);
        setError(null);

        try {
            const token = await getAuthToken();
            if (!token) {
                throw new Error('Nie ste prihlásený');
            }

            // Build batch request
            const documentTypes = pendingDocuments.map(doc => doc.type) as string[];
            const versions: Record<string, string> = {};
            for (const doc of pendingDocuments) {
                versions[doc.type] = doc.currentVersion;
            }

            // Add profiling if user chose "accept all"
            if (includesProfiling && showProfilingOption) {
                documentTypes.push('profiling');
                versions['profiling'] = PROFILING_CONSENT_VERSION;
            }

            const response = await fetch(`${config.apiUrl}/acceptLegalDocument`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Auth-Token': `Bearer ${token}`
                },
                body: JSON.stringify({
                    documentTypes,
                    versions
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Nepodarilo sa uložiť súhlas');
            }

            onAcceptAll();

        } catch (err: any) {
            console.error('Failed to accept documents:', err);
            setError(err.message || 'Nepodarilo sa uložiť súhlas. Skúste to znova.');
        } finally {
            setLoading(false);
        }
    };

    const documentIcons: Record<string, string> = {
        tos: '\uD83D\uDCC4',
        privacy: '\uD83D\uDD12',
        ai_notice: '\uD83E\uDD16'
    };

    // Full document viewer modal
    if (showFullDocument) {
        const displayName = DOCUMENT_NAMES[showFullDocument.type] || showFullDocument.type;
        return (
            <div style={styles.overlay}>
                <div style={styles.fullDocumentContainer}>
                    <div style={styles.fullDocumentHeader}>
                        <h2 style={styles.title}>{displayName}</h2>
                        <button
                            onClick={() => setShowFullDocument(null)}
                            style={styles.closeButton}
                            aria-label="Zavrieť"
                        >
                            &#x2715;
                        </button>
                    </div>
                    <div style={styles.fullDocumentContent} className="legal-document-markdown">
                        <style>{`
                            .legal-document-markdown h1 { font-size: 24px; margin-top: 0; margin-bottom: 16px; color: #2c3e50; }
                            .legal-document-markdown h2 { font-size: 20px; margin-top: 24px; margin-bottom: 12px; color: #2c3e50; }
                            .legal-document-markdown h3 { font-size: 17px; margin-top: 20px; margin-bottom: 8px; color: #34495e; }
                            .legal-document-markdown p { margin-bottom: 12px; line-height: 1.7; }
                            .legal-document-markdown ul, .legal-document-markdown ol { margin-bottom: 12px; padding-left: 24px; }
                            .legal-document-markdown li { margin-bottom: 6px; line-height: 1.6; }
                            .legal-document-markdown table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
                            .legal-document-markdown th, .legal-document-markdown td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; font-size: 14px; }
                            .legal-document-markdown th { background-color: #f8f9fa; font-weight: 600; }
                            .legal-document-markdown strong { font-weight: 600; }
                        `}</style>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{showFullDocument.content}</ReactMarkdown>
                    </div>
                    <div style={styles.fullDocumentFooter}>
                        <button
                            onClick={() => setShowFullDocument(null)}
                            style={styles.secondaryButton}
                        >
                            Zavrieť
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Title
    const title = isFirstTime
        ? 'Vitajte v SmartBloček!'
        : 'Aktualizovali sme dokumenty';

    // Intro text
    const introText = isFirstTime
        ? 'Aktualizovali sme nasledovné dokumenty súvisiace s prevádzkou SmartBloček.sk:'
        : 'Aktualizovali sme naše právne dokumenty. Pred pokračovaním si prosím prečítajte zmeny.';

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h2 style={styles.title}>{title}</h2>
                </div>

                <div style={styles.content}>
                    <p style={styles.introText}>{introText}</p>

                    {/* Mandatory documents section */}
                    <div style={styles.sectionLabel}>Povinné dokumenty</div>

                    {pendingDocuments.map((doc, index) => (
                        <div key={doc.type} style={index > 0 ? styles.documentSectionWithDivider : styles.documentSection}>
                            <div style={styles.documentHeader}>
                                <span style={styles.documentEmoji}>
                                    {documentIcons[doc.type] || '\uD83D\uDCC4'}
                                </span>
                                <div>
                                    <h3 style={styles.documentTitle}>
                                        {DOCUMENT_NAMES[doc.type] || doc.type}
                                    </h3>
                                    <span style={styles.versionBadge}>Verzia {doc.currentVersion}</span>
                                </div>
                            </div>

                            {/* Show changes if it's an update */}
                            {!isFirstTime && documentsChanges[doc.type] && (
                                <div style={styles.changesSection} className="legal-changes-markdown">
                                    <style>{`
                                        .legal-changes-markdown h2 { font-size: 18px !important; margin-top: 0; }
                                        .legal-changes-markdown h3 { font-size: 16px !important; }
                                    `}</style>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{documentsChanges[doc.type]}</ReactMarkdown>
                                </div>
                            )}

                            <button
                                onClick={() => handleViewFullDocument(doc.type)}
                                style={styles.linkButton}
                            >
                                {isFirstTime ? 'Prečítať dokument' : 'Zobraziť celý dokument'}
                            </button>
                        </div>
                    ))}

                    {/* Optional profiling consent section */}
                    {showProfilingOption && (
                        <div style={styles.profilingSection}>
                            <div style={styles.sectionLabel}>Voliteľný súhlas</div>
                            <div style={styles.profilingBox}>
                                <h3 style={styles.profilingTitle}>Profilovací súhlas</h3>
                                <p style={styles.profilingDescription}>
                                    Súhlasom získate napríklad tieto funkcie:
                                </p>
                                <ul style={styles.profilingList}>
                                    <li>Marketingové e-maily s tipmi a novinkami</li>
                                    <li>Mesačné prehľady vašich výdavkov (AI analýza)</li>
                                    <li>Sledovanie otvorení a kliknutí v e-mailoch pre lepšiu personalizáciu</li>
                                </ul>
                                <p style={styles.profilingNote}>
                                    Tento súhlas je voliteľný a môžete ho kedykoľvek odvolať v nastaveniach profilu.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Error message */}
                    {error && (
                        <div style={styles.errorBox}>
                            {error}
                        </div>
                    )}

                    {/* Info message */}
                    <div style={styles.warningBox}>
                        Bez súhlasu s povinnými dokumentmi nie je možné používať SmartBloček
                    </div>
                </div>

                <div style={styles.footer}>
                    {/* Two equal-weight buttons */}
                    <div style={styles.buttonRow}>
                        <button
                            onClick={handleAcceptMandatory}
                            disabled={loading}
                            style={{
                                ...styles.acceptButton,
                                ...(loading ? styles.buttonDisabled : {})
                            }}
                        >
                            {loading ? 'Spracúvam...' : (showProfilingOption ? 'Súhlasím s povinnými' : 'Súhlasím a pokračovať')}
                        </button>

                        {showProfilingOption && (
                            <button
                                onClick={handleAcceptAll}
                                disabled={loading}
                                style={{
                                    ...styles.acceptButton,
                                    ...(loading ? styles.buttonDisabled : {})
                                }}
                            >
                                {loading ? 'Spracúvam...' : 'Súhlasím so všetkým'}
                            </button>
                        )}
                    </div>

                    <button
                        onClick={handleLogout}
                        disabled={loading}
                        style={styles.logoutButton}
                    >
                        Odhlásiť sa
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    },
    fullDocumentContainer: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    },
    fullDocumentHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 30px',
        borderBottom: '1px solid #e0e0e0',
    },
    fullDocumentContent: {
        flex: 1,
        overflowY: 'auto',
        padding: '30px',
    },
    fullDocumentFooter: {
        padding: '20px 30px',
        borderTop: '1px solid #e0e0e0',
        textAlign: 'center',
    },
    header: {
        padding: '25px 30px',
        borderBottom: '1px solid #e0e0e0',
    },
    title: {
        margin: 0,
        fontSize: '22px',
        fontWeight: 600,
        color: '#2c3e50',
    },
    content: {
        flex: 1,
        overflowY: 'auto',
        padding: '25px 30px',
    },
    introText: {
        fontSize: '16px',
        lineHeight: '1.6',
        color: '#34495e',
        marginBottom: '20px',
    },
    sectionLabel: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#7f8c8d',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '12px',
    },
    documentSection: {
        marginBottom: '16px',
    },
    documentSectionWithDivider: {
        marginBottom: '16px',
        paddingTop: '16px',
        borderTop: '1px solid #e0e0e0',
    },
    documentHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '8px',
    },
    documentEmoji: {
        fontSize: '24px',
    },
    documentTitle: {
        margin: '0 0 4px 0',
        fontSize: '16px',
        fontWeight: 600,
        color: '#2c3e50',
    },
    versionBadge: {
        display: 'inline-block',
        fontSize: '12px',
        color: '#667eea',
        backgroundColor: '#eef2ff',
        padding: '2px 8px',
        borderRadius: '10px',
        fontWeight: 500,
    },
    changesSection: {
        backgroundColor: '#f8f9fa',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '8px',
        fontSize: '14px',
        lineHeight: '1.6',
    },
    linkButton: {
        background: 'none',
        border: 'none',
        color: '#3498db',
        fontSize: '14px',
        cursor: 'pointer',
        textDecoration: 'underline',
        padding: 0,
        display: 'block',
    },
    profilingSection: {
        marginTop: '24px',
        paddingTop: '20px',
        borderTop: '2px solid #e0e0e0',
    },
    profilingBox: {
        backgroundColor: '#f0f7ff',
        border: '1px solid #bcd4f0',
        borderRadius: '8px',
        padding: '16px',
    },
    profilingTitle: {
        margin: '0 0 8px 0',
        fontSize: '16px',
        fontWeight: 600,
        color: '#2c3e50',
    },
    profilingDescription: {
        fontSize: '14px',
        color: '#34495e',
        margin: '0 0 8px 0',
    },
    profilingList: {
        fontSize: '14px',
        color: '#34495e',
        margin: '0 0 12px 0',
        paddingLeft: '20px',
        lineHeight: '1.8',
    },
    profilingNote: {
        fontSize: '13px',
        color: '#7f8c8d',
        margin: 0,
        fontStyle: 'italic',
    },
    errorBox: {
        backgroundColor: '#fee',
        border: '1px solid #fcc',
        color: '#c33',
        padding: '12px',
        borderRadius: '6px',
        marginBottom: '15px',
        fontSize: '14px',
    },
    warningBox: {
        backgroundColor: '#fff9e6',
        border: '1px solid #ffe0a3',
        color: '#996600',
        padding: '12px',
        borderRadius: '6px',
        fontSize: '13px',
        textAlign: 'center',
        marginTop: '16px',
    },
    footer: {
        padding: '20px 30px',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    buttonRow: {
        display: 'flex',
        gap: '12px',
    },
    acceptButton: {
        flex: 1,
        backgroundColor: '#3498db',
        color: '#fff',
        border: 'none',
        padding: '14px 16px',
        fontSize: '15px',
        fontWeight: 600,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    logoutButton: {
        backgroundColor: 'transparent',
        color: '#7f8c8d',
        border: '1px solid #bdc3c7',
        padding: '10px 30px',
        fontSize: '14px',
        fontWeight: 500,
        borderRadius: '8px',
        cursor: 'pointer',
        width: '100%',
        transition: 'all 0.2s',
    },
    secondaryButton: {
        backgroundColor: '#95a5a6',
        color: '#fff',
        border: 'none',
        padding: '12px 30px',
        fontSize: '15px',
        fontWeight: 600,
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    buttonDisabled: {
        backgroundColor: '#bdc3c7',
        cursor: 'not-allowed',
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '28px',
        color: '#7f8c8d',
        cursor: 'pointer',
        padding: 0,
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
};

export default LegalConsentModal;
